import { CoverageItem, FormikTermData, Insured } from "@/common/data/formikData";
import { UiHint } from "./productInfoInterfaces";
import { PremiumPaymentMode, Sex } from "./quoteInterfaces";
import { IRenderModelContext } from "@/components/term/contexts/renderModelProvider";
import { IProductInfoContext } from "@/common/context/productInfoProvider";
import { PolicyLevelRiderInsuredId} from "@/common/utils/constants";
import { areAllPolicyRiderFieldsPresent, isBasePlan, isFaceAmountProvidedForEditRider } from "./illustrationValidation";
import { ICoverageIllustrationDto, IInsuredIllustrationDto, IQuoteIllustrationDto, IllustrationPremiumMode, IllustrationGender, IllustrationLanguageCode, IllustrationProduct } from "./illustrationInterface";
import { getFormattedDate, isNumberValue } from "../utils/helpers";

export const illustrationModelToDto = (
    model: FormikTermData,
    productInfo: IProductInfoContext,
    renderModel: IRenderModelContext,
    language: any,
    generateIllustration: boolean = false
): IQuoteIllustrationDto => {
    const result: IQuoteIllustrationDto = {
        id: model.id,
        number: model.number,
        productId: model.productId,
        productName: productInfo.data.name,
        productVariantId: model.productVariantId,
        productVariantName: productInfo.mappedData.productVariants[model.productVariantId!]?.name,
        premiumMode: model.premiumPaymentMode === PremiumPaymentMode.Monthly ? IllustrationPremiumMode.Monthly : IllustrationPremiumMode.Annual,
        productCode: IllustrationProduct.Term,
        language: language + IllustrationLanguageCode.LanguageCode,
        generateIllustration: generateIllustration,
        coverages: [],
        insureds: [],
        advisorCode: model.advisorCode,
        advisorFirstName: model.advisorFirstName,
        advisorLastName: model.advisorLastName,
        systemDate: getFormattedDate(language)
    };

    // update insureds
    result.insureds = model.insureds.map((insured: Insured) => (
        {
            id: insured.id?.toString(),
            backDateAge: insured.useBackDate,
            gender: insured.sex === Sex.Male ? IllustrationGender.Male : IllustrationGender.Female,
            dateOfBirth: insured.dateOfBirth,
            age: insured.age!,
            firstName: insured.firstName,
            lastName: insured.lastName,
            isSmoker: insured.isSmoker!,
            permFlatExtraAmt: insured.ratingInfo?.permanent,
            tempFlatExtraAmt: insured.ratingInfo?.temporal,
            tempFlatExtraDuration: insured.ratingInfo?.durationYears,
            permTableRating: productInfo.data.ratingPercentages.find(r => r.id == insured.ratingInfo?.percentageId)
            ?.value.toString()
        }
    ));

    // add policy level rider Insured and update the insured list
    if ((model.policyRider !=null && areAllPolicyRiderFieldsPresent(model?.policyRider)) || (model.editPolicyRider !=null && areAllPolicyRiderFieldsPresent(model?.editPolicyRider))) {
        let policyRiderInfo = model.editPolicyRider ? model.editPolicyRider?.payorDisabilityWaiverOfPremiumRiderInfo : model.policyRider?.payorDisabilityWaiverOfPremiumRiderInfo;
        if(generateIllustration && model.policyRider?.payorDisabilityWaiverOfPremiumRiderInfo)
        {
            policyRiderInfo =  model.policyRider?.payorDisabilityWaiverOfPremiumRiderInfo;
        }
        const insured: IInsuredIllustrationDto = {
            id: PolicyLevelRiderInsuredId.toString(),
            backDateAge: policyRiderInfo?.useBackDate,
            gender: policyRiderInfo?.sex === Sex.Male ? IllustrationGender.Male : IllustrationGender.Female,
            dateOfBirth: policyRiderInfo?.dateOfBirth,
            age: policyRiderInfo?.age!,
            firstName: policyRiderInfo?.firstName,
            lastName: policyRiderInfo?.lastName,
            isSmoker: policyRiderInfo?.isSmoker!
        }
        result.insureds.push(insured);
    }
    
    const allInsuredIds = model.insureds.map((a: Insured) => a.id);

    const mapCoverageItemToCoverage = (
        insuredId: number,
        coverageItem: CoverageItem
    ): ICoverageIllustrationDto => {
        const plan = productInfo.mappedData.plans[coverageItem.planId];
        
        let cirProductCode = '';
        let permTableRating = null;
        if (coverageItem.criticalIllnessRiderInfo != null) {
            cirProductCode = productInfo.mappedData.criticalIllnessRiderPremiumTypes[coverageItem.criticalIllnessRiderInfo.premiumTypeId!].code;
            permTableRating =
                productInfo.data.ratingPercentages
                    .find(r => r.id == coverageItem.ratingInfo?.percentageId)
                    ?.value.toString() ?? null;
        }

        return{
            id: `${new Date().getTime()}-${Math.floor(Math.random() * 1000000)}`, //This is required for illustration purpose as illustration service requires unique id to be passed for coverages where as the coverages in formik has only 0 as ID for all coverages.
            planId: coverageItem.planId?.toString(),
            planName: plan?.name,
            lifeIds: (model.isJoint && insuredId !== PolicyLevelRiderInsuredId && isBasePlan(plan)) ? allInsuredIds.map((val: number) => String(val)) : [insuredId].map((val) => String(val)),
            faceAmount: coverageItem.faceAmount,
            isBasePlan: isBasePlan(plan),
            expiryAge: coverageItem.criticalIllnessRiderInfo?.expiryAge?.toString(),
            productCode: coverageItem.uiHint === UiHint.Cir ? cirProductCode! : plan?.code!,
            permTableRating: permTableRating
        }
    };

    let editRider = null;
    if (model?.editRider && model.editRider?.planId &&
        isNumberValue(model?.editRiderInsuredIndex) &&
        !generateIllustration &&
        isFaceAmountProvidedForEditRider(model.editRider) &&
        !renderModel.validation.editRider.hasErrors
    ) {
        const insured = model.insureds[model.editRiderInsuredIndex];
        editRider = mapCoverageItemToCoverage(insured.id, model?.editRider);
    }

    // update coverages
    result.coverages = model.insureds
        .map((insured: Insured, insuredIndex: number) =>
            insured.basePlanItems // take all base plans
                .filter(() => insuredIndex === 0 || !model.isJoint) // for joint take base plans only for first insured
                .map((b) => mapCoverageItemToCoverage(insured.id, b)) // convert them to ICoverageIllustrationDto
                .concat(
                    // concat them with riders, converted to ICoverageIllustrationDto as well
                    insured.riderItems
                        .filter(b => 
                            editRider == null || 
                            editRider.planId !== b.planId.toString() || 
                            model.editRiderInsuredIndex !== insuredIndex
                        )
                        .map(b => mapCoverageItemToCoverage(insured.id, b))
                )
                .concat(
                    editRider != null && model.editRiderInsuredIndex === insuredIndex 
                        ? [editRider] 
                        : []
                    ) // include the rider being edited
        )
        .flatMap(a => a); // convert ICoverageIllustrationDto[][] --->  ICoverageIllustrationDto[]

    if (model?.editPolicyRider && areAllPolicyRiderFieldsPresent(model?.editPolicyRider) && !generateIllustration) {
        //policy level rider requires first insured id
        const cov = mapCoverageItemToCoverage(PolicyLevelRiderInsuredId, model?.editPolicyRider);
        result.coverages.push(cov);
    }
    
    if (model?.editPolicyRider == null && model?.policyRider && areAllPolicyRiderFieldsPresent(model?.policyRider)){
        //policy level rider requires first insured id
        const cov = mapCoverageItemToCoverage(PolicyLevelRiderInsuredId, model.policyRider);
        result.coverages.push(cov);
    }

    return result;
}

export const getPremiumAmount = (coverages: any, planId: any, mode: any, insuredId:number) => {
    if (coverages && coverages.length > 0) {
        let index = coverages?.findIndex((i: { planId: string; lifeIds: string[]; }) => +i.planId === planId && i.lifeIds.includes(insuredId.toString()));
        return  index >= 0 ? coverages[index]?.[mode] : 0;
    }
    else return 0;
}